import React, { useRef, useEffect, useState } from 'react';
import ZoomModal from '../common/ZoomInModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ZoomPopupProps {
    isOpen: boolean;
    zoomData: {
        zoomId: string;
        title: string;
        content: string;
        book_uri: string;
    };
    zoomIds: Set<{ id: string; page_no: string }>;
    onClose: (pageNum?: string) => void;
}

const ZoomPopup: React.FC<ZoomPopupProps> = ({
    isOpen,
    zoomData,
    zoomIds,
    onClose,
}) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [iframeSrc, setIframeSrc] = useState<string>('');
    const [currentHotspotIndex, setCurrentHotspotIndex] = useState<number>(0);
    const zoomIdsArray = Array.from(zoomIds);

    useEffect(() => {
        const zoomId = zoomData?.zoomId;
        const hotspotIndex = zoomIdsArray.findIndex(
            (item) => item.id === zoomId
        );
        setCurrentHotspotIndex(hotspotIndex);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoomData]);

    useEffect(() => {
        if (zoomIdsArray[currentHotspotIndex]) {
            const src = `${zoomData?.book_uri}/OPS/${zoomIdsArray[currentHotspotIndex].id}.xhtml`;
            setIframeSrc(src);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentHotspotIndex]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                const pageNum = zoomIdsArray[currentHotspotIndex]?.page_no;
                onClose(pageNum);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClose, currentHotspotIndex]);
    const curr_book = useSelector((state: RootState) => state.currentBook.name);
    const curr_page = useSelector(
        (state: RootState) => state.currentBook.currentPage
    );
    const handleHotspotNext = () => {
        if (currentHotspotIndex < zoomIdsArray.length - 1) {
            setCurrentHotspotIndex(currentHotspotIndex + 1);
        }
    };

    const handleHotspotPrev = () => {
        if (currentHotspotIndex > 0) {
            setCurrentHotspotIndex(currentHotspotIndex - 1);
        }
    };

    const footerButtons = [
        <button
            data-testid="hotspot-viewer-prev-button"
            key="prev"
            className="w-[180px] h-[73px] bg-blue-500 text-white rounded-tl-[24px] opacity-1"
            style={{
                padding: '21px 40px',
                borderRadius: '24px',
                margin: '0 10px',
            }}
            onClick={handleHotspotPrev}
        >
            Prev
        </button>,
        <button
            key="next"
            data-testid="hotspot-viewer-next-button"
            className="w-[180px] h-[73px] bg-blue-500 text-white rounded-tl-[24px] opacity-1"
            style={{
                padding: '21px 40px',
                borderRadius: '24px',
                margin: '0 10px',
            }}
            onClick={handleHotspotNext}
        >
            Next
        </button>,
    ];

    const handleClose = () => {
        const pageNum = zoomIdsArray[currentHotspotIndex]?.page_no;
        onClose(pageNum); // Send page number on close
    };

    return (
        <ZoomModal
            title="Zoomed"
            isOpen={isOpen}
            onClose={handleClose} // Use updated onClose
            footerButtons={footerButtons}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}
            >
                <iframe
                    ref={iframeRef}
                    src={iframeSrc}
                    style={{
                        border: 'none',
                        width: '80%',
                        height: '80%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}
                    title="Zoomed Content"
                />
            </div>
        </ZoomModal>
    );
};

export default ZoomPopup;
